// Tasks (FD §16) — work for agents. Shown per project (/p/:key/tasks) with a
// Ticket and a Stream column; the workspace-wide page at /tasks remains only as
// the fallback for a workspace without projects (router.tsx redirects it
// otherwise). Webhook-originated tasks (Plane/GitHub) only ever appear in the
// list; the "New task" drawer creates manual ones (FD §16.1). The task drawer
// itself lives in components/tasks/task-drawer.tsx.

import { useState } from 'react'
import { useQueries, useQuery } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import type { Project, Task } from '@goblic/shared'
import { TaskSource, TaskStatus } from '@goblic/shared'
import { streamsApi, tasksApi, workflowsApi } from '../lib/api-client'
import { StatusBadge } from '../components/status-badge'
import {
  SOURCE_LABEL,
  TaskCreateDrawer,
  TaskDrawer,
  isLive,
  isStartable,
  LIVE_REFRESH_MS,
  useStartTask,
  type TaskDrawerTab,
} from '../components/tasks/task-drawer'
import { QUEUE_LANE_LABEL } from '../components/board/board-utils'
import { SubtaskCountBadge } from '../components/tasks/subtasks'
import {
  EmptyState,
  ErrorNote,
  LoadingRows,
  PageHeader,
  Tabs,
} from '../components/ui'
import { useWorkspaceId } from '../lib/auth'
import { useLookups } from '../hooks/useLookups'
import { formatRelative, humanize } from '../lib/format'
import { sceneClass } from '../components/goblin-art'

/** Search params of the tasks pages (`/tasks` and `/p/:key/tasks`, router.tsx). */
export interface TasksSearch {
  new?: boolean
  taskId?: string
  /** Which tab the `taskId` drawer opens on. */
  tab?: TaskDrawerTab
  /** Only tasks of this stream (key). */
  stream?: string
}

type TaskView = 'open' | 'assigned' | 'completed' | 'rejected' | 'all'

const VIEW_STATUSES: Record<TaskView, TaskStatus[] | null> = {
  open: [TaskStatus.Pending, TaskStatus.Validating, TaskStatus.Blocked],
  assigned: [
    TaskStatus.Assigned,
    TaskStatus.InProgress,
    TaskStatus.AwaitingApproval,
  ],
  completed: [TaskStatus.Completed],
  rejected: [TaskStatus.Rejected],
  all: null,
}

/** The task table of a project (or of the whole workspace without `project`), with its drawers driven by `search`. */
export function TasksView({
  project,
  search,
  onSearchChange,
}: {
  project?: Project
  search: TasksSearch
  onSearchChange: (search: TasksSearch) => void
}) {
  const workspaceId = useWorkspaceId()
  const lookups = useLookups()
  const [view, setView] = useState<TaskView>('open')
  const [source, setSource] = useState<string>('')
  // The list shows main tasks only; subtasks live on their parent's drawer.
  const [showSubtasks, setShowSubtasks] = useState(false)
  const startMutation = useStartTask()
  const creating = search.new === true && project != null

  const filters = {
    ...(project ? { projectId: project.id } : {}),
    ...(showSubtasks ? {} : { topLevel: true }),
  }
  const tasksQuery = useQuery({
    queryKey: ['tasks', workspaceId, filters],
    queryFn: () => tasksApi.list({ workspaceId, ...filters }),
    enabled: workspaceId !== '',
    // While any task is moving, keep the list live; idle lists don't poll.
    refetchInterval: (query) => (query.state.data?.items.some(isLive) ? LIVE_REFRESH_MS : false),
  })
  const streamsQuery = useQuery({
    queryKey: ['project', project?.id ?? '', 'streams'],
    queryFn: () => streamsApi.list(project!.id),
    enabled: project != null,
  })
  const streams = streamsQuery.data?.items ?? []

  const allTasks = tasksQuery.data?.items ?? []
  const byFilter = allTasks.filter(
    (t) => (!source || t.source === source) && (!search.stream || t.streamKey === search.stream)
  )
  const inView = (v: TaskView) =>
    VIEW_STATUSES[v] ? byFilter.filter((t) => VIEW_STATUSES[v]!.includes(t.status)) : byFilter
  const tasks = inView(view)

  // Blocked workflow tasks may have ended in a terminal state: load the
  // workflows of their projects to tell which can be retried.
  const blockedWorkflowProjects = [
    ...new Set(
      allTasks
        .filter((t) => t.status === TaskStatus.Blocked && t.workflowKey)
        .map((t) => t.projectId)
    ),
  ]
  const workflowQueries = useQueries({
    queries: blockedWorkflowProjects.map((projectId) => ({
      queryKey: ['project', projectId, 'workflows'],
      queryFn: () => workflowsApi.get(projectId),
    })),
  })
  function startable(task: Task): boolean {
    return isStartable(
      task,
      workflowQueries[blockedWorkflowProjects.indexOf(task.projectId)]?.data?.workflows
    )
  }

  function openNew(): void {
    onSearchChange({ new: true, ...(search.stream ? { stream: search.stream } : {}) })
  }
  const keepStream = search.stream ? { stream: search.stream } : {}

  const description = project
    ? `Work for your agents in ${project.name}. Tasks from outside systems arrive on their own; you can also add one by hand.`
    : 'Work for your agents. Tasks from Plane arrive here on their own; you can also add one by hand.'

  return (
    <div>
      <PageHeader
        title="Tasks"
        description={description}
        actions={
          project ? (
            <button type="button" className="btn-primary" onClick={openNew}>
              New task
            </button>
          ) : undefined
        }
      />

      <div className={`panel ${sceneClass('archive')}`}>
        <div className="flex flex-wrap items-end justify-between gap-3 px-4 pt-3">
          <Tabs
            label="Task status"
            value={view}
            onChange={setView}
            options={[
              { value: 'open', label: 'Open', count: inView('open').length },
              {
                value: 'assigned',
                label: 'In progress',
                count: inView('assigned').length,
              },
              { value: 'completed', label: 'Done' },
              { value: 'rejected', label: 'Rejected' },
              { value: 'all', label: 'All' },
            ]}
          />
          <div className="mb-2 flex flex-wrap items-center gap-4">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-3">
              <input
                type="checkbox"
                checked={showSubtasks}
                onChange={(event) => setShowSubtasks(event.target.checked)}
              />
              Show subtasks
            </label>
            {project && streams.length > 1 ? (
              <label className="flex items-center gap-2 text-sm text-ink-3">
                Stream
                <select
                  className="field-input h-8 w-auto py-0"
                  value={search.stream ?? ''}
                  onChange={(e) =>
                    onSearchChange(e.target.value ? { stream: e.target.value } : {})
                  }
                >
                  <option value="">All streams</option>
                  {streams.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            <label className="flex items-center gap-2 text-sm text-ink-3">
              From
              <select
                className="field-input h-8 w-auto py-0"
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                <option value="">Anywhere</option>
                {Object.values(TaskSource).map((s) => (
                  <option key={s} value={s}>
                    {SOURCE_LABEL[s] ?? humanize(s)}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {tasksQuery.isLoading ? (
          <LoadingRows />
        ) : tasksQuery.isError ? (
          <ErrorNote error={tasksQuery.error} what="tasks" />
        ) : tasks.length === 0 ? (
          <EmptyState
            title={view === 'open' ? 'No open tasks' : 'Nothing here'}
            action={
              view === 'open' && project ? (
                <button type="button" className="btn-secondary" onClick={openNew}>
                  Add a task
                </button>
              ) : undefined
            }
          >
            {view === 'open'
              ? 'Add a task by hand, or let an outside system send issues here through the API.'
              : 'No tasks match this filter.'}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Ticket</th>
                  <th>Task</th>
                  <th>Stream</th>
                  <th>From</th>
                  <th>Risk</th>
                  <th>Status</th>
                  <th className="w-px" />
                </tr>
              </thead>
              <tbody>
                {tasks.map((task) => (
                  <tr
                    key={task.id}
                    data-clickable
                    onClick={() => onSearchChange({ taskId: task.id, tab: 'details', ...keepStream })}
                  >
                    <td className="whitespace-nowrap font-mono text-sm text-ink-2">
                      {task.ticketKey ?? '—'}
                    </td>
                    <td>
                      <span className="block font-medium text-ink">
                        {task.title}
                        <SubtaskCountBadge summary={task.subtaskSummary} />
                      </span>
                      <span className="block text-sm text-ink-3">
                        {task.subtaskKey ? `Subtask ${task.subtaskKey} · ` : ''}
                        {project ? '' : `${lookups.projectName(task.projectId)}, `}
                        added {formatRelative(task.createdAt)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      {task.streamKey ? (
                        <>
                          {streams.find((s) => s.key === task.streamKey)?.name ??
                            humanize(task.streamKey)}
                          {task.queueLane ? (
                            <span className="mt-0.5 block text-sm text-ink-3">
                              {QUEUE_LANE_LABEL[task.queueLane]}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td>{SOURCE_LABEL[task.source] ?? humanize(task.source)}</td>
                    <td>
                      <StatusBadge status={task.riskLevel} label={humanize(task.riskLevel)} />
                    </td>
                    <td>
                      <StatusBadge status={task.status} />
                      {task.workflowState ? (
                        <span className="mt-0.5 block text-sm text-ink-3">
                          {humanize(task.workflowState)}
                        </span>
                      ) : null}
                    </td>
                    <td className="text-right">
                      {startable(task) ? (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          disabled={startMutation.isPending}
                          onClick={(event) => {
                            event.stopPropagation()
                            startMutation.mutate(task.id)
                          }}
                        >
                          {task.status === TaskStatus.Blocked ? 'Retry' : 'Start run'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && project ? (
        <TaskCreateDrawer
          project={project}
          workflowKey={streams.find((s) => s.key === search.stream)?.workflowKey ?? null}
          onClose={() => onSearchChange(keepStream)}
          onCreated={(task) => onSearchChange({ taskId: task.id, tab: 'details', ...keepStream })}
        />
      ) : null}

      {search.taskId ? (
        <TaskDrawer
          taskId={search.taskId}
          initialTab={search.tab}
          onClose={() => onSearchChange(keepStream)}
        />
      ) : null}
    </div>
  )
}

/** `/tasks` without projects: the workspace-wide table (every task has a project, so this is normally empty). */
export function TasksPage() {
  const navigate = useNavigate()
  const search = useSearch({ from: '/_authenticated/tasks' })
  return (
    <TasksView
      search={search}
      onSearchChange={(next) => void navigate({ to: '/tasks', search: next, replace: true })}
    />
  )
}
